import {
  Component,
  inject,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  DestroyRef,
  signal,
    effect,OnInit
} from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {TranslatePipe,TranslateService,_} from '@ngx-translate/core';
import { emailValidator } from '~shared/validators/email.validator';
import { codeValidator} from "~shared/validators/otp.validator"
import { user } from '~core/types/session.type';
import {user_response} from "~shared/models/user_response.model";
import { passwordValidator } from '~shared/validators/password.validator';
import { LowercaseDirective } from '~shared/directives/lowercase.directive';
import { TrimDirective } from '~shared/directives/trim.directive';
import type { ApiErrorResponse } from '~shared/types/api-response.types';
import { API_ERROR_CODES } from '~core/constants/api-error-codes.constants';
import { AlertService } from '~core/services/ui/alert.service';
import type {LogInFormGroup,LogInFormState, MfaFormGroup} from '~features/authentication/types/login-form.types';

import { CartService} from "~shared/services/cart.service";
import { LanguageSelectorComponent} from "~shared/components/language-selector/language-selector.component/language-selector.component";
import { SessionService} from "~core/services/session.service";
import {LoginService} from "~shared/services/login.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {EMPTY, finalize} from "rxjs";
import {catchError} from "rxjs/operators";
import {LoginRequestOTP} from "~features/authentication/types/login-request.type";
import {LoginResponseData, LoginResponseOTPData} from "~shared/types/login-response.type";
import { SpinnerVisibilityService } from 'ng-http-loader';
import {RequiredShipmentService} from "~shared/services/required_shipment.service";

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [ ReactiveFormsModule,
    LowercaseDirective,
    TrimDirective,TranslatePipe, LanguageSelectorComponent],
  templateUrl: './login.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class LoginComponent implements OnInit {
  sessionService: SessionService = inject(SessionService);
  cartService = inject(CartService);
  private readonly loginService: LoginService = inject(LoginService);
  private router: Router = inject(Router);
  private readonly alertService = inject(AlertService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly spinner = inject(SpinnerVisibilityService);
  private readonly requiredShipmentService = inject(RequiredShipmentService);

  readonly logInForm = this.createLoginForm();
  readonly formControls = {
    email: this.logInForm.get('email') as FormControl<string>,
    password: this.logInForm.get('password') as FormControl<string>,
  };
  readonly formState = signal<LogInFormState>({
    isLoading: false,
    isSubmitted: false,
  });
  QRCode=signal<string>("");
  readonly mfaForm = this.createOtpForm();
  readonly mfaControls = {
    mfa_code: this.mfaForm.get('mfa_code') as FormControl<string>,
    mfa_flag: this.mfaForm.get('mfa_flag') as FormControl<boolean>,
  };

  constructor() {
    this.cartService.clearCart();
    effect(() => {
      if (this.formState().isLoading) {
        this.spinner.show();
      } else {
        this.spinner.hide();
      }
    });
  }

  ngOnInit(): void {
     this.formState.set({ isLoading: false, isSubmitted: false });
     this.isMfaStep.set(false);
     this.QRCode.set("");
    }
;


  /* non usato
    loginData = {
      email: '',
      password: ''
    };

    mfaData = {
      user_id:'',
      mfa_code:0,
      mfa_flag:false
    };
  */

  isMfaStep = signal<boolean>(false);
  private email:string = '';

  private createLoginForm(): LogInFormGroup {
    return this.formBuilder.group({
      email: new FormControl<string>('', {
        validators: [Validators.required, Validators.minLength(4), emailValidator()],
        nonNullable: true,
      }),
      password: new FormControl<string>('', {
        validators: [Validators.required, Validators.minLength(6), passwordValidator()],
        nonNullable: true,
      }),
    });
  }

  private createOtpForm(): MfaFormGroup {
    return this.formBuilder.group({
      mfa_code: new FormControl<string>('', {
        validators: [Validators.required, Validators.minLength(6), codeValidator()],
        nonNullable: true,
      }),
      mfa_flag: new FormControl<boolean>(false, {
        nonNullable: true,
      })
    });
  }


  private handleLoginError(response: ApiErrorResponse): void {
    const errorMessage =
        response.error?.internalCode === API_ERROR_CODES.INVALID_CREDENTIALS_CODE
            ? 'loginCredentialsError'
            : 'genericErrorAlert';
    this.translate.get(_(errorMessage))
        .subscribe((text: string) =>
        {
          this.alertService.createErrorAlert(`${text}`);
        });
  }

  private updateFormState(updates: Partial<LogInFormState>): void {
    //console.log('Updating form state:', updates);
    return;
    //this.formState.update((state) => ({ ...state, ...updates }));
  }

  sendLogin(): void {
    //console.log('Sending login request');
    this.updateFormState({ isSubmitted: true });
    //console.log('Form is valid, proceeding with login request', this.logInForm.getRawValue());
    if (this.logInForm.invalid) {
      //console.log('Form is invalid, skipping login request');
      this.logInForm.markAllAsTouched();
      this.updateFormState({ isSubmitted: false });
      return;
    }
    this.email='';
    this.updateFormState({ isLoading: true });
    //console.log('Sending login request with form data:', this.logInForm.getRawValue());
    try {
      this.loginService
          .login(this.logInForm.getRawValue())
          .pipe(
              takeUntilDestroyed(this.destroyRef),
              finalize(() => {
                this.updateFormState({isLoading: false});
              }),
              catchError((error: ApiErrorResponse) => {
                this.handleLoginError(error);
                return EMPTY;
              }),
          )
          .subscribe({
            next: (data: LoginResponseData) => {
              if (data) {
                if (!data.mfa_required) {
                  this.router.navigate(['/']);
                } else {
                  this.email = this.logInForm.getRawValue().email;
                 // console.log("QRCODE:",data.qrcode??"");
                  this.QRCode.set(data.qrcode??"");
                  this.isMfaStep.set(true);
                  return;
                }
              } else {
                this.translate.get(_("loginCredentialsError"))
                    .subscribe((text: string) => {
                      this.alertService.createErrorAlert(`${text}`);
                    });
              }
            },
          });
    }
    catch (error:any){
      console.error('Error during login:', error);
      this.translate.get(_("genericErrorAlert"))
          .subscribe((text: string) => {
            this.alertService.createErrorAlert(`${text}`);
          });
    }
  }
/*
  doLogin() {
    const needsMfa =
      this.requestMfa || this.loginData.email.toLowerCase().includes('+mfa');

    if (needsMfa) {
      this.isMfaStep = true;
      return;
    }

    this.router.navigate(['home']);
  }
*/
  sendMfa(): void {
    //console.log('Sending MFA request');
    this.updateFormState({ isSubmitted: true });
    if (this.mfaForm.invalid) {
      this.mfaForm.markAllAsTouched();
      return;
    }
    //console.log('Sending MFA request with form data:', this.mfaForm.getRawValue());
    if (!this.email ) {
      this.translate.get(_("UserDefinedError"))
          .subscribe((text: string) =>
          {
            this.alertService.createErrorAlert(`${text}`);
          });
        return;
    }
    let mfarequest:LoginRequestOTP = {...this.mfaForm.getRawValue(),email:this.email};
    this.updateFormState({ isLoading: true });
    try {
      this.loginService
          .checkMfa(mfarequest)
          .pipe(
              takeUntilDestroyed(this.destroyRef),
              finalize(() => {
                this.updateFormState({isLoading: false});
              }),
              catchError((error: ApiErrorResponse) => {
                this.handleLoginError(error);
                return EMPTY;
              }),
          )
          .subscribe({
            next: (data: LoginResponseOTPData) => {
            //  console.log("Risposta MFA:",data)
              if (data.mfa_check) {
                this.router.navigate(['/']);
              } else {
                this.translate.get(_("otpCodeError"))
                    .subscribe((text: string) => {
                      this.alertService.createErrorAlert(`${text}`);
                    });
              }
            },
          });
    }
    catch (error:any){
      console.error('Error during login:', error);
      this.translate.get(_("genericErrorAlert"))
          .subscribe((text: string) => {
            this.alertService.createErrorAlert(`${text}`);
          });
    }
  }

  /*
  private AssegnaUtente(user:user_response){
      let userData:user={
        email:user.email,
        role:user.role,
        token:user.token,
        companies:user.companies,
        defaultcompany:user.defaultcompany,
        permissions:user.permissions,
        prezzi_ivati:user.prezzi_ivati,};
      this.sessionService.setUser(userData)
      this.requiredShipmentService.requestOpen();
  }*/
}
